import { Vector2 } from "@babylonjs/core";
import _ from "lodash";

export interface MeshAngleInfo {
  clockwiseAngleRadians: number;
  shortestAngleRadians: number;
}

export interface MeshAudioDetails {
  distance: number;
  sourceToTarget: MeshAngleInfo;
  targetToSource: MeshAngleInfo;
}

export interface SourceAngleConfig {
  innerConeAngleRadiansAtMinDistance: number;
  innerConeAngleRadiansAtMaxDistance: number;
  innerConeMaxDistance: number;
  outerConeAngleRadians: number;
  outerConeMinReduction: number;
  outerConeMaxReduction: number;
  innerConeMinAmplification: number;
  innerConeMaxAmplification: number;
  outerConeBufferForMinValue: number;
}

export interface TargetAngleConfig {
  innerConeAngleRadians: number;
  outerConeAngleRadians: number;
  outerConeMinReduction: number;
  outerConeMaxReduction: number;
  outerConeBufferForMinValue: number;
}

export class PanningConfig {
  minValue: number;
  maxValue: number;
  minRange: number;
  maxRange: number;

  constructor(minValue = 0.65, maxValue = 0.95, minRange = 3, maxRange = 20) {
    this.minValue = minValue;
    this.maxValue = maxValue;
    this.minRange = minRange;
    this.maxRange = maxRange;
  }
}

export const DEFAULT_PANNING_CONFIG = new PanningConfig();
export const NO_PANNING_CONFIG = new PanningConfig(0, 0, 0, 200);
export const AGGRESSIVE_PANNING_CONFIG = new PanningConfig(0.85, 1, 3, 10);

export class LowPanConfig {
  minRange: number;
  maxRange: number;
  minValue: number;
  maxValue: number;
  valueAtDistance: Vector2[];
  source: SourceAngleConfig;
  target: TargetAngleConfig;

  constructor(
    minRange = 6,
    maxRange = 30,
    minValue = 40,
    maxValue = 24000,
    valueAtDistance = [new Vector2(10, 8100), new Vector2(13.5, 2700), new Vector2(17, 900), new Vector2(20.5, 300), new Vector2(24, 100)],
    source = {
      innerConeAngleRadiansAtMinDistance: Math.PI/8,
      innerConeAngleRadiansAtMaxDistance: Math.PI/12,
      innerConeMaxDistance: 20,
      outerConeAngleRadians: Math.PI * 2/3,
      outerConeMinReduction: 4,
      outerConeMaxReduction: 8,
      innerConeMinAmplification: 3,
      innerConeMaxAmplification: 7,
      outerConeBufferForMinValue: Math.PI/12,
    },
    target = {
      innerConeAngleRadians: Math.PI/3,
      outerConeAngleRadians: Math.PI * 2/3,
      outerConeMinReduction: 1.5,
      outerConeMaxReduction: 3,
      outerConeBufferForMinValue: Math.PI/12,
    },
  ) {
    this.minRange = minRange;
    this.maxRange = maxRange;
    this.minValue = minValue;
    this.maxValue = maxValue;
    this.source = source;
    this.target = target;
    this.valueAtDistance = [new Vector2(minRange, maxValue), ...valueAtDistance, new Vector2(maxRange, minValue)];
  }
}

export const DEFAULT_LOWPASS_CONFIG = new LowPanConfig();
export const INVERTED_LOWPASS_CONFIG = new LowPanConfig(
  1,
  200,
  24000,
  40,
  [new Vector2(10, 100), new Vector2(13.5, 300), new Vector2(17, 900), new Vector2(20.5, 2700), new Vector2(24, 8100)],
  {
    innerConeAngleRadiansAtMinDistance: Math.PI/4,
    innerConeAngleRadiansAtMaxDistance: Math.PI/9,
    innerConeMaxDistance: 20,
    outerConeAngleRadians: Math.PI * 2/3,
    outerConeMinReduction: 0,
    outerConeMaxReduction: 0,
    innerConeMinAmplification: 0,
    innerConeMaxAmplification: 0,
    outerConeBufferForMinValue: Math.PI/12,
  },
  {
    innerConeAngleRadians: Math.PI/3,
    outerConeAngleRadians: Math.PI * 2/3,
    outerConeMinReduction: 1.5,
    outerConeMaxReduction: 3,
    outerConeBufferForMinValue: Math.PI/12,
  },
);

export class VolumeConfig {
  distanceMinRange: number;
  sourceAngleConfig: SourceAngleConfig;
  targetAngleConfig: TargetAngleConfig;

  constructor(
    distanceMinRange = 4,
    sourceAngleConfig: SourceAngleConfig = {
      innerConeAngleRadiansAtMaxDistance: Math.PI/12,
      innerConeAngleRadiansAtMinDistance: Math.PI/8,
      outerConeAngleRadians: Math.PI/2,
      innerConeMaxDistance: 20,
      outerConeMinReduction: 0.20,
      outerConeMaxReduction: 0.50,
      innerConeMinAmplification: 0.15,
      innerConeMaxAmplification: 4.00,
      outerConeBufferForMinValue: Math.PI/12,
    },
    targetAngleConfig: TargetAngleConfig = {
      innerConeAngleRadians: Math.PI/4,
      outerConeAngleRadians: Math.PI * 2/3,
      outerConeMinReduction: 0.10,
      outerConeMaxReduction: 0.30,
      outerConeBufferForMinValue: Math.PI/12,
    },
  ) {
    this.distanceMinRange = distanceMinRange;
    this.sourceAngleConfig = sourceAngleConfig;
    this.targetAngleConfig = targetAngleConfig;
  }
}

export const DEFAULT_VOLUME_CONFIG = new VolumeConfig();
export const INVERTED_VOLUME_CONFIG = new VolumeConfig(
  0,
  {
    innerConeAngleRadiansAtMaxDistance: Math.PI/12,
    innerConeAngleRadiansAtMinDistance: Math.PI/6,
    outerConeAngleRadians: Math.PI/2,
    innerConeMaxDistance: 30,
    outerConeMinReduction: 0,
    outerConeMaxReduction: 0,
    innerConeMinAmplification: 0.10,
    innerConeMaxAmplification: 0.35,
    outerConeBufferForMinValue: Math.PI/12,
  },
  {
    innerConeAngleRadians: Math.PI/4,
    outerConeAngleRadians: Math.PI * 2/3,
    outerConeMinReduction: 0.05,
    outerConeMaxReduction: 0.35,
    outerConeBufferForMinValue: Math.PI/12,
  },
);
